import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';

import { Icons, PuzzleNav } from '@/js/libs/ui/src/V2';
import { PrivateContent, Roles, useIdentityUser, or, not } from '@/js/libs/use-auth';

import { ROUTER } from 'src/AppRouter';
import useAppLogout from 'src/hooks/useAppLogout';
import { AccountButton } from './AccountButton';
import { FastTranslateButton } from './FastTranslateButton';
import { LanguageDropdown } from './LanguageDropdown';
import { StatusButton } from './StatusButton';
import { ActionGroup, Wrapper } from './styled';
import { SupportButton } from './SupportButton';
import { ThemeButton } from './ThemeButton';

type FooterProps = {
  isCollapsedNav: boolean;
};

export const Footer = memo(function Footer({ isCollapsedNav }: FooterProps) {
  const [t] = useTranslation(['common', 'components']);

  const user = useIdentityUser();
  const logout = useAppLogout();

  return (
    <div>
      {isCollapsedNav && (
        <PrivateContent roles={not(Roles.ANONYMOUS)}>
          <PuzzleNav.ItemSeparator standalone />
          <PuzzleNav.Item
            standalone
            icon={<Icons.Person />}
            title={user?.firstName ? `${user.firstName.charAt(0)}. ${user.lastName}` : t('common:PROFILE')}
            itemKey='account'
            path={ROUTER['/account'].path.pattern}
            dataTestId='profile-button'>
            <PrivateContent roles={not(Roles.SSO)}>
              <PuzzleNav.ItemSubLink
                title={t('components:MANAGE_ACCOUNT')}
                to={ROUTER['/account/edit-settings'].path.pattern}
                dataTestId='manage-account-button'
              />
            </PrivateContent>
            <PrivateContent roles={or(Roles.ADMIN, Roles.COMPANY_ADMIN)}>
              <PuzzleNav.ItemSubLink
                title={t('components:COMPANY_EDIT')}
                to={ROUTER['/account/company'].path.pattern}
                dataTestId='manage-company-button'
              />
              <PrivateContent roles={not(or(Roles.SSO, Roles.POSTADMANAGER))}>
                <PuzzleNav.ItemSubLink
                  title={t('components:COMPANY_USERS')}
                  to={ROUTER['/account/users'].path.pattern}
                  dataTestId='user-administration-button'
                />
              </PrivateContent>
            </PrivateContent>
            <PuzzleNav.ItemSubLink title={t('components:LOGOUT')} onClick={logout} dataTestId='user-logout-button' />
          </PuzzleNav.Item>
        </PrivateContent>
      )}

      <Wrapper>
        <StatusButton isCollapsedNav={isCollapsedNav} />

        <CSSTransition in={!isCollapsedNav} timeout={250} unmountOnExit>
          <ActionGroup $isCollapsedNav={isCollapsedNav}>
            <FastTranslateButton />
            <ThemeButton />
            <LanguageDropdown />
            <AccountButton />
            <SupportButton />
          </ActionGroup>
        </CSSTransition>
      </Wrapper>
    </div>
  );
});
